import { stickerUrl } from "@listou/shared/stickers";

import type { BlogPost } from "../../lib/blog";
import { DOWNLOADS, INLINE_CTA, LETTERS } from "../../lib/blog-markers";

export const lettreAuPereNoel: BlogPost = {
  h1: "Lettre au Père Noël à imprimer : 3 modèles gratuits, et tous nos conseils",
  seoTitle: "Lettre au Père Noël à imprimer (PDF gratuit) : 3 modèles par âge · Listou",
  seoDescription:
    "Trois lettres au Père Noël à imprimer gratuitement, en PDF : pour les 3-5 ans, les 6-8 ans et les plus grands. Avec des modèles à recopier, l'adresse pour recevoir une réponse et comment en faire une liste pour la famille.",
  publishedOn: "2026-10-26",
  updatedOn: "2026-10-26",
  summary:
    "Imprimez l'une de nos trois lettres au Père Noël, une par âge, et laissez l'enfant la remplir. Une lettre au Père Noël tient en quelques lignes : bonjour, ce que l'enfant a fait de bien cette année, trois à cinq souhaits, et un merci. Envoyée par la poste avec l'adresse de l'expéditeur au dos, elle reçoit une réponse, sans timbre. Pour que la famille sache quoi offrir, recopiez ensuite les souhaits sur une liste en ligne : la lettre part au Pôle Nord, la liste aux grands-parents.",
  body: `
## Nos lettres au Père Noël à imprimer

Trois lettres à remplir, une par âge, avec un cadre pour le dessin et des cases à cocher. Imprimez-la en A4, en couleur si vous pouvez : le cadre en sucre d'orge et le timbre du Pôle Nord font tout le charme.
${DOWNLOADS}
Elles sont gratuites, sans inscription, et serviront encore l'an prochain : elles ne portent pas d'année.

## Quand écrire la lettre

**Fin novembre ou tout début décembre.** Assez tôt pour que la réponse arrive avant Noël, et pour que la famille ait le temps d'acheter. Assez tard pour que l'enfant n'ait pas changé d'avis trois fois avant le 24.

Beaucoup de familles en font un rituel : on l'écrit le premier week-end de l'Avent, on la poste ensemble, et on attend la réponse.

## Que mettre dans la lettre

Une bonne lettre a toujours la même trame, à adapter à l'âge :

1. **Bonjour**, et le prénom et l'âge de l'enfant.
2. **Une nouvelle de l'année** : ce qu'il a appris, ce dont il est fier.
3. **Trois à cinq souhaits**, pas plus. Le Père Noël ne peut pas tout porter.
4. **Une pensée pour les autres** : un souhait pour un frère, une sœur, ou pour les rennes.
5. **Merci**, une signature et un dessin.

Limiter les souhaits apprend à choisir, et évite la déception d'une lettre de vingt jouets.

## Trois lettres pour s'inspirer

Pas de modèle parfait : la meilleure lettre est celle qui ressemble à l'enfant. Voici trois lettres, une par âge, pour donner le ton.
${LETTERS}
## Avec un enfant qui ne sait pas encore écrire

Il dicte, vous écrivez. Laissez-le ensuite :

- **dessiner** ses souhaits à côté des mots ;
- **découper et coller** les images des jouets dans un catalogue ;
- **signer** à sa façon, avec son prénom recopié ou une empreinte de main.

C'est souvent cette lettre-là que les parents gardent des années plus tard.

## Où envoyer la lettre au Père Noël

Glissez-la dans une enveloppe adressée simplement au **Père Noël**, et postez-la dans n'importe quelle boîte aux lettres. **Pas besoin de timbre.** Depuis les années 1960, La Poste répond aux enfants qui lui écrivent.

Deux conditions pour recevoir une réponse :

- **l'adresse de l'expéditeur**, écrite lisiblement au dos de l'enveloppe ;
- **un envoi assez tôt** : La Poste annonce chaque année sa date limite, et une lettre postée fin novembre ou début décembre arrive largement à temps.

La Poste propose aussi d'écrire la lettre en ligne, sur son site, pour une réponse par email.

## Faire de la lettre une liste pour la famille

La lettre part au Pôle Nord, mais la famille a besoin de savoir quoi acheter, et en un seul exemplaire. Le plus simple :

1. **Recopiez chaque souhait** sur la liste de Noël de l'enfant, avec le lien du jouet exact quand vous l'avez trouvé.
2. **Gardez les coups de cœur de la lettre** en tête de liste.
3. **Ajoutez quelques idées** pour les proches qui offrent en plus, avec des petits prix.
4. **Envoyez un seul lien** aux grands-parents, aux oncles et tantes. Chacun réserve le cadeau qu'il offre, et personne n'achète deux fois le même camion de pompier.
${INLINE_CTA}
Les souhaits de la lettre qui viennent « du Père Noël » restent ceux des parents : gardez-les hors de la liste partagée, ou réservez-les vous-mêmes pour que personne d'autre ne les prenne. Pour d'autres idées selon l'âge, notre guide de la [liste de Noël par âge](/blog/liste-de-noel-enfant-idees-par-age) donne des pistes de 1 à 14 ans.

## Et la réponse du Père Noël

Si l'enfant est assez grand pour se demander comment le Père Noël lit toutes les lettres, profitez-en pour lui raconter qu'il a des lutins secrétaires. Et gardez la réponse avec la lettre : dans dix ans, elles feront sourire toute la famille.
`,
  faq: [
    [
      "Où trouver une lettre au Père Noël à imprimer ?",
      "Listou propose trois lettres gratuites en PDF, à imprimer en A4 : une pour les 3 à 5 ans, à dessiner et colorier, une pour les 6 à 8 ans, et une pour les plus grands, avec plus de place pour écrire. Sans inscription.",
    ],
    [
      "Quelle adresse mettre sur une lettre au Père Noël ?",
      "Il suffit d'écrire « Père Noël » sur l'enveloppe, sans timbre, et de mettre l'adresse de l'expéditeur au dos pour recevoir une réponse. La Poste se charge du reste.",
    ],
    [
      "Faut-il un timbre pour écrire au Père Noël ?",
      "Non, la lettre au Père Noël part sans timbre depuis n'importe quelle boîte aux lettres. L'adresse de l'expéditeur au dos est indispensable pour recevoir la réponse.",
    ],
    [
      "Quand envoyer sa lettre au Père Noël ?",
      "Fin novembre ou début décembre, pour que la réponse arrive avant Noël. La Poste annonce chaque année la date limite pour recevoir une réponse.",
    ],
    [
      "Combien de souhaits mettre dans une lettre au Père Noël ?",
      "Trois à cinq. L'enfant apprend à choisir, et la lettre reste lisible. Les autres idées peuvent aller sur sa liste de Noël pour la famille.",
    ],
    [
      "Comment faire la lettre au Père Noël avec un enfant qui ne sait pas écrire ?",
      "Il dicte, vous écrivez, puis il dessine ses souhaits, colle les images des jouets découpées dans un catalogue et signe à sa façon.",
    ],
    [
      "Comment partager la lettre au Père Noël avec la famille ?",
      "Recopiez les souhaits sur une liste de Noël en ligne et envoyez le lien aux proches. Sur Listou, chacun réserve le cadeau qu'il offre, et l'enfant ne voit pas ce qui a été pris.",
    ],
  ],
  occasion: "christmas",
  inlineCta: {
    title: "La lettre au Pôle Nord, la liste à la famille",
    text: "Recopiez les souhaits, envoyez un seul lien. Chacun réserve, personne n'achète deux fois le même jouet.",
    sticker: stickerUrl("mariage", "enveloppe"),
  },
  downloads: [
    {
      title: "Pour les 3 à 5 ans",
      text: "Presque rien à écrire : il colorie son âge, trace sa main et dessine ses souhaits.",
      file: "lettre-au-pere-noel-petits",
    },
    {
      title: "Pour les 6 à 8 ans",
      text: "Cinq souhaits, une étoile pour le préféré, et ce qu'il laissera pour les rennes.",
      file: "lettre-au-pere-noel",
    },
    {
      title: "Pour les 9 ans et plus",
      text: "Sa plus belle aventure, ses souhaits classés, et une blague pour les lutins.",
      file: "lettre-au-pere-noel-grands",
    },
  ],
  letters: [
    {
      label: "Léo, 4 ans, dicté à papa",
      paper: "dessin",
      lines: [
        "Cher Père Noël,",
        "Je m'appelle Léo et j'ai 4 ans.",
        "Cette année j'ai fait du vélo sans les petites roues. Même pas tombé.",
        "Je voudrais un camion de pompier qui fait pin-pon, un livre de dinosaures et des feutres.",
        "Fais un bisou à Rodolphe.",
      ],
      signature: "LÉO",
      stickers: [stickerUrl("vehicules", "camion-pompier"), stickerUrl("dinos", "trex")],
    },
    {
      label: "Jade, 7 ans, au stylo plume",
      paper: "seyes",
      lines: [
        "Cher Père Noël,",
        "Cette année j'ai lu mon premier vrai roman toute seule, avec des chapitres.",
        "Pour Noël je voudrais un jeu pour jouer avec mon petit frère (même s'il triche), des perles pour faire des bracelets et le tome 3 de ma série.",
        "Couvre-toi bien pendant ta tournée !",
      ],
      signature: "Jade",
      stickers: [stickerUrl("licornes", "etoile"), stickerUrl("rayons-grands", "livres")],
    },
    {
      label: "Hugo, 10 ans, tout seul",
      paper: "lignes",
      lines: [
        "Cher Père Noël,",
        "Je sais que tu es débordé, alors je fais court.",
        "Cette année je suis entré au club de basket et on a gagné notre premier match (12 à 10).",
        "Dans l'ordre : un vrai ballon, un kit de chimie, une BD. Et une séance de cinéma avec mes parents, s'il reste de la place dans le traîneau.",
        "PS : comment tu fais pour les cheminées ?",
      ],
      signature: "Hugo",
      stickers: [stickerUrl("noel", "bonnet")],
    },
  ],
};
